import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, dt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Power, Save, ShieldCheck, Store } from "lucide-react";
import { toast } from "sonner";

const HEALTH = {
  ok: "bg-success/15 text-success",
  near_limit: "bg-warning/15 text-warning",
  limit_reached: "bg-destructive/15 text-destructive",
  stalled: "bg-destructive/15 text-destructive",
  paused: "bg-muted text-muted-foreground",
  auto_paused: "bg-warning/15 text-warning",
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b py-2 last:border-0">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-right break-all">{value ?? "—"}</span>
  </div>
);

export default function BankAccounts() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const showMerchant = user.role !== "merchant";
  const canEdit = ["merchant", "super_admin", "admin"].includes(user.role);

  const load = useCallback(() => {
    api.get("/bank-accounts").then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const bankTypes = [...new Set((rows || []).map((v) => v.bank_type || v.bank_label).filter(Boolean))];
  const filtered = (rows || []).filter((v) =>
    (fType === "all" || (v.bank_type || v.bank_label) === fType) &&
    (fStatus === "all" || (v.approval_status || (v.active ? "ACTIVE" : "INACTIVE")) === fStatus)
  );

  const pool = (rows || []).reduce((a, v) => {
    a.total += 1;
    if (v.active) a.active += 1;
    if (v.active && (v.health === "ok" || v.health === "near_limit")) a.rotation += 1;
    if (v.verified) a.verified += 1;
    return a;
  }, { total: 0, active: 0, rotation: 0, verified: 0 });

  const toggleVerified = async () => {
    try {
      const { data } = await api.patch(`/bank-accounts/${sel.id}`, { verified: !sel.verified });
      toast.success(sel.verified ? "Marked unverified" : "Marked verified — preferred in rotation");
      setSel({ ...sel, ...data });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const mqOf = (v) => !!v && (v.merchant_qr === true || (v.merchant_qr == null && /(paytmqr|@ptys$|@ptm$)/i.test(v.vpa || "")));

  const toggleMerchantQr = async () => {
    const next = !mqOf(sel);
    try {
      const { data } = await api.patch(`/bank-accounts/${sel.id}`, { merchant_qr: next });
      toast.success(next ? "Merchant-QR link ON (Paytm P2M intent)" : "Merchant-QR link OFF (plain P2P)");
      setSel({ ...sel, ...data });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openDetails = (v) => {
    setSel(v);
    setEdit({ daily_limit: v.daily_limit ?? 0, daily_max_count: v.daily_max_count ?? "", deposit_min: v.deposit_min ?? "", deposit_max: v.deposit_max ?? "", monthly_max: v.monthly_max ?? "", mcc: v.mcc ?? "" });
  };

  const saveLimits = async () => {
    setSaving(true);
    try {
      const body = {};
      ["daily_limit", "daily_max_count", "deposit_min", "deposit_max", "monthly_max"].forEach((k) => {
        if (edit[k] !== "" && edit[k] != null) body[k] = parseFloat(edit[k]);
      });
      body.mcc = edit.mcc?.trim() || null;
      const { data } = await api.patch(`/bank-accounts/${sel.id}`, body);
      toast.success("Limits updated");
      setSel({ ...sel, ...data });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleActive = async () => {
    try {
      const { data } = await api.patch(`/bank-accounts/${sel.id}`, { active: !sel.active });
      toast.success(data.active ? "Account activated" : "Account deactivated");
      setSel({ ...sel, ...data });
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Bank accounts</h2>
        <p className="text-sm text-muted-foreground">Every UPI ID in rotation, grouped by bank provider.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pool-health-banner">
        {[
          { k: "total", label: "Total UPI IDs", val: pool.total, cls: "" },
          { k: "active", label: "Active", val: pool.active, cls: "text-success" },
          { k: "rotation", label: "In rotation (healthy)", val: pool.rotation, cls: "text-primary" },
          { k: "verified", label: "Verified", val: pool.verified, cls: "text-primary" },
        ].map((s) => (
          <Card key={s.k} className="p-4" data-testid={`pool-stat-${s.k}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className={`mt-1 font-heading text-2xl font-bold ${s.cls}`}>{s.val}</p>
          </Card>
        ))}
      </div>
      {rows && pool.rotation <= 1 && pool.total > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning" data-testid="pool-warmup-warning">
          Low healthy pool — add more UPI IDs so blocked or limit-reached accounts don't stall collections.
        </div>
      )}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="space-y-1"><label className="text-xs text-muted-foreground">Bank type</label>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger className="w-48" data-testid="ba-filter-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All bank types</SelectItem>{bankTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><label className="text-xs text-muted-foreground">Approval status</label>
            <Select value={fStatus} onValueChange={setFStatus}>
              <SelectTrigger className="w-44" data-testid="ba-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="INACTIVE">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="text-xs text-muted-foreground pb-2" data-testid="ba-filter-count">{filtered.length} account(s)</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bank provider</TableHead>
                <TableHead>UPI ID / Owner</TableHead>
                {showMerchant && <TableHead>Merchant</TableHead>}
                <TableHead className="text-right">Pay counters</TableHead>
                <TableHead>Health</TableHead>
                <TableHead className="text-right">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {rows && filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No bank accounts match.</TableCell></TableRow>}
              {rows && filtered.map((v) => (
                <TableRow key={v.id} data-testid={`bank-account-row-${v.id}`}>
                  <TableCell className="font-semibold">{v.provider}</TableCell>
                  <TableCell><div className="font-mono text-xs">{v.vpa}</div><div className="text-xs text-muted-foreground">{v.owner_name || v.payee_name}</div></TableCell>
                  {showMerchant && <TableCell className="text-sm">{v.merchant_name || "—"}</TableCell>}
                  <TableCell className="text-right font-mono text-xs" data-testid={`bank-counters-${v.id}`}>
                    <div>{v.count_today || 0} ({v.daily_max_count ? v.daily_max_count : "∞"})</div>
                    <div className="text-muted-foreground">{inr(v.sum_today)} ({v.daily_limit ? inr(v.daily_limit) : "∞"})</div>
                  </TableCell>
                  <TableCell><Badge className={HEALTH[v.health] || "bg-muted"}>{(v.health || "ok").replace("_", " ")}</Badge></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => openDetails(v)} data-testid={`bank-details-${v.id}`}>Details</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="bank-details-modal">
          <DialogHeader><DialogTitle>Account information</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-1">
              <Row label="Bank type" value={sel.bank_type || sel.bank_label} />
              <Row label="Bank provider" value={sel.provider} />
              <Row label="Legal form" value={sel.legal_form} />
              <Row label="Owner name" value={sel.owner_name || sel.payee_name} />
              <Row label="UPI ID (VPA)" value={sel.vpa} />
              <Row label="Account number" value={sel.account_number} />
              <Row label="Linked phone" value={sel.linked_phone} />
              <Row label="Linked E-mail" value={sel.linked_email} />
              <Row label="SMS application Client ID" value={sel.sms_client_id} />
              <Row label="Forward OTP email to" value={sel.forward_otp_to} />
              <Row label="Approval status" value={<Badge className={sel.active ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"}>{sel.approval_status || (sel.active ? "ACTIVE" : "INACTIVE")}</Badge>} />
              <Row label="Verified" value={<Badge className={sel.verified ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}>{sel.verified ? "Verified" : "Not verified"}</Badge>} />
              <Row label="Payment link" value={<Badge className={mqOf(sel) ? "bg-amber-500/15 text-amber-600" : "bg-muted text-muted-foreground"}>{mqOf(sel) ? "Merchant QR (Paytm)" : "Standard P2P"}</Badge>} />
              <Row label="Created" value={dt(sel.created_at)} />
              <Row label="Total received" value={inr(sel.total_received)} />
              <Row label="Pay counter (today)" value={`${sel.txn_count || 0} txns · ${inr(sel.received_today)}`} />

              {canEdit && (
                <div className="rounded-xl border bg-muted/30 p-3 mt-3 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Editable limits</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><label className="text-xs">Daily max SUM (₹)</label><Input type="number" value={edit.daily_limit} onChange={(e) => setEdit({ ...edit, daily_limit: e.target.value })} data-testid="edit-daily-sum" /></div>
                    <div className="space-y-1"><label className="text-xs">Daily max COUNT</label><Input type="number" value={edit.daily_max_count} onChange={(e) => setEdit({ ...edit, daily_max_count: e.target.value })} data-testid="edit-daily-count" /></div>
                    <div className="space-y-1"><label className="text-xs">Deposit min (₹)</label><Input type="number" value={edit.deposit_min} onChange={(e) => setEdit({ ...edit, deposit_min: e.target.value })} data-testid="edit-deposit-min" /></div>
                    <div className="space-y-1"><label className="text-xs">Deposit max (₹)</label><Input type="number" value={edit.deposit_max} onChange={(e) => setEdit({ ...edit, deposit_max: e.target.value })} data-testid="edit-deposit-max" /></div>
                    <div className="space-y-1 col-span-2"><label className="text-xs">Monthly max payments (₹)</label><Input type="number" value={edit.monthly_max} onChange={(e) => setEdit({ ...edit, monthly_max: e.target.value })} data-testid="edit-monthly-max" /></div>
                    <div className="space-y-1 col-span-2"><label className="text-xs">Merchant category code (MCC) — Paytm/merchant VPAs</label><Input value={edit.mcc} onChange={(e) => setEdit({ ...edit, mcc: e.target.value })} placeholder="e.g. 4722" data-testid="edit-mcc" /></div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {canEdit && sel && (
              <>
                <Button variant="outline" onClick={toggleMerchantQr} data-testid="bank-toggle-merchantqr" className={mqOf(sel) ? "text-amber-600 border-amber-500/40" : ""}>
                  <Store className="mr-1.5 h-4 w-4" />{mqOf(sel) ? "Set P2P" : "Set Merchant QR"}
                </Button>
                <Button variant="outline" onClick={toggleVerified} data-testid="bank-toggle-verified" className={sel.verified ? "text-primary border-primary/40" : ""}>
                  <ShieldCheck className="mr-1.5 h-4 w-4" />{sel.verified ? "Unverify" : "Mark verified"}
                </Button>
                <Button variant={sel.active ? "destructive" : "default"} onClick={toggleActive} data-testid="bank-toggle-active">
                  <Power className="mr-1.5 h-4 w-4" />{sel.active ? "Deactivate" : "Activate"}
                </Button>
                <Button onClick={saveLimits} disabled={saving} data-testid="bank-save-limits">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save limits</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
